import React, { useState, useCallback, useEffect } from "react";
import {
  Button,
  Stack,
  Layout,
  Toast,
  Spinner,
  Modal,
  ChoiceList,
} from "@shopify/polaris";
import {
  useSearchParams,
  useNavigate,
  createSearchParams,
} from "react-router-dom";

import {
  BookingFlowFrame,
  BookingFlowPage,
} from "@src/components";
import { API_BASE_URL } from "@src/config";
import {
  Analytics,
  Appointment,
  CalendlyResponse,
  Customer,
  SelectedProduct,
  useBooking,
} from "@src/core";
import { routes } from "@src/routes";
import {
  getGeoInformation,
  validateEmail,
  validatePhoneNumber,
  getRawPhoneNumber,
} from "@src/utils";

import { AppointmentDetails } from "./appointment";
import { ContactInfo } from "./customer";
import { SelectedProductCard } from "./selected-product-card";
import "./styles.scss";

const initialFieldValues = {
  firstName: {
    value: "",
    errorMsg: "",
    fieldFormalName: "First name",
    isRequired: true,
  },
  lastName: {
    value: "",
    errorMsg: "",
    fieldFormalName: "Last name",
    isRequired: true,
  },
  email: {
    value: "",
    errorMsg: "",
    fieldFormalName: "Email",
    isRequired: true,
  },
  phone: {
    value: "",
    errorMsg: "",
    fieldFormalName: "Phone number",
    isRequired: true,
  },
  address: {
    value: "",
    errorMsg: "",
    fieldFormalName: "Address",
    isRequired: true,
  },
  aptUnit: {
    value: "",
    errorMsg: "",
    fieldFormalName: "Apt/Unit",
    isRequired: false,
  },
  city: {
    value: "",
    errorMsg: "",
    fieldFormalName: "City",
    isRequired: true,
  },
  zipCode: {
    value: "",
    errorMsg: "",
    fieldFormalName: "Postal Code",
    isRequired: true,
  },
  gateCode: {
    value: "",
    errorMsg: "",
    fieldFormalName: "Gate code",
    isRequired: false,
  },
};

export function SummaryPage({}) {
  const navigate = useNavigate();

  const { setUrlToken, useAsyncError } = useBooking();

  const [isLoading, setIsLoading] = useState(true);
  const throwError = useAsyncError();

  // Error Toast
  const [toast, setToast] = useState({
    isActive: false,
    message: "",
    isError: false,
  });
  const toggleToastActive = useCallback(
    () => setToast({ ...toast, isActive: !toast.isActive }),
    [toast]
  );

  const [calendlySearchParams, setCalendlySearchParams] = useSearchParams();
  const [customer, setCustomer] = useState(Customer.fromJSON({}));
  const [appointment, setAppointment] = useState(Appointment.fromJSON({}));

  const [geoCodedAddress, setGeoCodedAddress] = useState(null);
  const [addressModalActive, setAddressModalActive] = useState(false);
  const [originalAddress, setOriginalAddress] = useState("");
  const [selectedAddress, setSelectedAddress] = useState([-1]);

  // Customer details
  const [isAddressInvalid, setIsAddressInvalid] = useState(false);
  const [inputFieldValues, setFormFieldValues] = useState(initialFieldValues);

  // Selected Product details
  const [selectedProduct, setSelectedProduct] = useState(
    SelectedProduct.fromJSON({})
  );

  /**
   * Initialize data part
   */

  const fetchCalendlyData = async () => {
    const phone_number = calendlySearchParams.get("phone_number");
    const invitee_uuid = calendlySearchParams.get("invitee_uuid");

    const booking_api_url = phone_number
      ? `${API_BASE_URL}booking/customer/${phone_number}`
      : `${API_BASE_URL}booking/calendly_invitee/${invitee_uuid}`;

    await fetch(booking_api_url, { cache: "no-store" })
      .then((response) => {
        if (response.ok) {
          return response.json();
        }
        throw new Error("Something went wrong fetching Customer");
      })
      .then((calendlyResponse) => {
        let customerObj = CalendlyResponse.fromJSON(calendlyResponse);
        customerObj.zipCode = customerObj.calendlyEvent.location.location;

        setCustomer(customerObj);
        setSelectedProduct(customerObj.selectedProduct);
        setAppointment({
          ...Appointment.fromJSON({}),
          date: customerObj.calendlyEvent.startTime,
        });

        setUrlToken(customerObj.urlToken);
        setIsLoading(false);
      })
      .catch((error) => {
        throwError(error);
      });
  };

  useEffect(() => {
    fetchCalendlyData().catch(console.error);
  }, []);

  useEffect(() => {
    if (inputFieldValues.zipCode.value == "") {
      inputFieldValues.zipCode.value = customer.zipCode;
      inputFieldValues.firstName.value = customer.firstName;
      inputFieldValues.lastName.value = customer.lastName;
      inputFieldValues.email.value = customer.email;
      inputFieldValues.phone.value = customer.phone;
      setFormFieldValues(initialFieldValues);
    }
  }, [customer]);

  /**
   * Event Handlers for product detail
   */
  // Event Handler for Change button from product detail
  const onClickSelectedProduct = () => {
    navigate({
      pathname: "/choose-product/",
      search: createSearchParams({
        url_token: customer.urlToken,
      }).toString(),
    });
  };

  const handleInputFieldsBlur = useCallback(
    (field, evt) => {
      let value = evt.target.value;
      if (value == "") return;

      setCustomer({
        ...customer,
        [field]: value,
      });
    },
    [customer]
  );

  const handleInputFieldsChanges = useCallback(
    (field, value) => {
      if (
        field === "address" ||
        field === "aptUnit" ||
        field === "city" ||
        field === "zipCode"
      ) {
        setIsAddressInvalid(false);
      }

      setFormFieldValues({
        ...inputFieldValues,
        [field]: {
          value: value,
          errorMsg: "",
          isRequired: inputFieldValues[field].isRequired,
          fieldFormalName: inputFieldValues[field].fieldFormalName,
        },
      });
    },
    [inputFieldValues]
  );

  const onClickConfirm = () => {
    var inputFieldValuesTemp = inputFieldValues;
    var isError = false;
    Object.keys(inputFieldValuesTemp).map((field, index) => {
      if (
        field === "phone" &&
        inputFieldValuesTemp[field].value !== "" &&
        !validatePhoneNumber(inputFieldValuesTemp[field].value)
      ) {
        isError = true;
        inputFieldValuesTemp = {
          ...inputFieldValuesTemp,
          [field]: {
            value: inputFieldValuesTemp[field].value,
            errorMsg: "Phone number format is incorrect",
            isRequired: inputFieldValuesTemp[field].isRequired,
            fieldFormalName: inputFieldValuesTemp[field].fieldFormalName,
          },
        };
      } else if (
        field === "email" &&
        !validateEmail(inputFieldValuesTemp[field].value)
      ) {
        isError = true;
        inputFieldValuesTemp = {
          ...inputFieldValuesTemp,
          [field]: {
            value: inputFieldValuesTemp[field].value,
            errorMsg: "Email format is incorrect",
            isRequired: inputFieldValuesTemp[field].isRequired,
            fieldFormalName: inputFieldValuesTemp[field].fieldFormalName,
          },
        };
      }

      if (
        inputFieldValuesTemp[field].isRequired &&
        inputFieldValuesTemp[field].value === ""
      ) {
        isError = true;
        inputFieldValuesTemp = {
          ...inputFieldValuesTemp,
          [field]: {
            value: inputFieldValuesTemp[field].value,
            errorMsg:
              inputFieldValuesTemp[field].fieldFormalName + " is required",
            isRequired: inputFieldValuesTemp[field].isRequired,
            fieldFormalName: inputFieldValuesTemp[field].fieldFormalName,
          },
        };
      }
    });

    setFormFieldValues(inputFieldValuesTemp);
    if (!isError) {
      const originalAddressTemp = inputFieldValues.aptUnit.value
        ? inputFieldValues.address.value +
          ", " +
          inputFieldValues.aptUnit.value +
          ", " +
          inputFieldValues.city.value +
          ", " +
          inputFieldValues.zipCode.value
        : inputFieldValues.address.value +
          ", " +
          inputFieldValues.city.value +
          ", " +
          inputFieldValues.zipCode.value;
      setOriginalAddress(originalAddressTemp);
      // Add country code to get only USA address
      getGeoInformation(originalAddressTemp + " USA", setGeoCodedAddress);
    }
  };

  /**
   * Addtional modal to check addres
   */
  const addressActiveModal = (
    <Modal
      open={addressModalActive}
      title="Verify address"
      onClose={() => {
        setAddressModalActive(false);
      }}
      primaryAction={{
        content: "Use selected address",
        onAction: () => {
          handleSubmit();
          setAddressModalActive(false);
        },
      }}
      secondaryActions={{
        content: "Edit",
        onAction: () => {
          setAddressModalActive(false);
        },
      }}
    >
      <Modal.Section>
        <Layout>
          <Layout.Section>
            <ChoiceList
              title="Suggested address"
              choices={geoCodedAddress?.map((geo, index) => {
                return {
                  label: geo.formatted_address,
                  value: index,
                };
              })}
              selected={selectedAddress}
              onChange={(value) => {
                setSelectedAddress(value);
              }}
            />
          </Layout.Section>
          <Layout.Section>
            <ChoiceList
              title="Original address"
              choices={[{ label: originalAddress, value: -1 }]}
              selected={selectedAddress}
              onChange={(value) => {
                setSelectedAddress([-1]);
              }}
            />
          </Layout.Section>
        </Layout>
      </Modal.Section>
    </Modal>
  );

  useEffect(() => {
    if (geoCodedAddress === null) {
      return;
    }

    if (geoCodedAddress.length == 0) {
      setIsAddressInvalid(true);
    } else {
      setAddressModalActive(true);
      setSelectedAddress([0]);
    }
  }, [geoCodedAddress]);

  const handleSubmit = async () => {
    var addressForSubmit;
    if (selectedAddress[0] == -1) {
      addressForSubmit = {
        address: originalAddress,
      };
    } else {
      addressForSubmit = {
        address:
          geoCodedAddress[selectedAddress[0]].formatted_address.split(",")[0],
        city: geoCodedAddress[selectedAddress[0]].city.long_name,
        state: geoCodedAddress[selectedAddress[0]].province_state.short_name,
        zip_code: geoCodedAddress[selectedAddress[0]].postal_code.short_name,
      };
    }

    const formattedPhone = getRawPhoneNumber(customer.phone);

    const settings = {
      method: "POST",
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        customer: {
          ...Customer.toJSON(customer),
          ...addressForSubmit,
          phone: formattedPhone,
        },
        appointment: {
          date: customer.calendlyEvent.startTime,
        },
        selected_product: SelectedProduct.toJSON(customer.selectedProduct),
      }),
    };

    let response = await fetch(
      API_BASE_URL + `booking/customer_job_request/`,
      settings
    );
    if (response.status != 200) {
      setToast({
        isActive: true,
        message: "There was an issue completing booking",
        isError: true,
      });

      return;
    }

    navigate({
      pathname: "/success/",
      search: createSearchParams({
        type: customer.productCriteria.tankType,
        firstname: customer.firstName,
      }).toString(),
    });
  };

  const handleBack = () => {
    const routeTo =
      customer.productCriteria.tankType == "tankless"
        ? routes.ScheduleTankless
        : routes.ScheduleTank;
    const urlToken = customer.urlToken;
    navigate(routeTo, {
      state: {
        urlToken,
      },
    });
  };

  const toastMarkup = toast.isActive ? (
    <Toast
      content={toast.message}
      error={toast.isError}
      onDismiss={toggleToastActive}
    />
  ) : null;

  function renderTermsOfService(isMobile) {
    const className = isMobile
      ? "caption-text small-screen"
      : "caption-text not-small-screen";
    return (
      <div className={className}>
        By scheduling, you agree to read and accept our{" "}
        <a href="">terms of service.</a> <br />
        Have questions now? Call us at{" "}
        <a href="tel:(949) 822-8229">(949) 822-8229</a>
      </div>
    );
  }

  function renderPageContent() {
    return isLoading ? (
      <div className="page-isloading">
        <Spinner accessibilityLabel="Spinner example" size="large" />
      </div>
    ) : (
      <div className={"summary-page-container"}>
        <Layout>
          <Layout.Section>
            <Stack vertical spacing="loose" distribution="trailing">
              <Stack.Item>
                <AppointmentDetails
                  appointment={appointment}
                  handleBack={handleBack}
                />
              </Stack.Item>
              <Stack.Item>
                <ContactInfo
                  handleInputFieldsBlur={handleInputFieldsBlur}
                  handleInputFieldsChanges={handleInputFieldsChanges}
                  inputFieldValues={inputFieldValues}
                  isAddressInvalid={isAddressInvalid}
                />
              </Stack.Item>
            </Stack>
          </Layout.Section>
          <Layout.Section secondary>
            <Stack vertical spacing="loose" distribution="trailing">
              <Stack.Item>
                <SelectedProductCard
                  onClickSelectedProduct={onClickSelectedProduct}
                  selectedProduct={selectedProduct}
                />
              </Stack.Item>
              <Stack.Item>{renderTermsOfService()}</Stack.Item>
            </Stack>
          </Layout.Section>
        </Layout>
        <Layout>
          <Layout.Section>
            <Stack vertical spacing="loose">
              <Stack.Item>
                <div className="primaryActionWrapper">
                  <Button primary={true} size="large" onClick={onClickConfirm}>
                    Confirm
                  </Button>
                </div>
              </Stack.Item>
              <Stack.Item>
                <div>{renderTermsOfService(true)}</div>
              </Stack.Item>
            </Stack>
          </Layout.Section>
        </Layout>
        {addressActiveModal}
      </div>
    );
  }

  const renderPage = (
    <BookingFlowPage
      title="Let's finalize your details"
      handleBack={handleBack}
    >
      {renderPageContent()}
      {toastMarkup}
    </BookingFlowPage>
  );

  return <BookingFlowFrame>{renderPage}</BookingFlowFrame>;
}
